import { Component, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import Swal from "sweetalert2";
import { catchError, of } from "rxjs";

import { Utils } from "../../../../../shared/utils/Utils";

import { PesvDesplazamientosService } from "../../../../../shared/services/pesvDesplazamientos.service";
import { PesvRequisitosDesplazamientos, PesvRequisitosDesplazamientosPayload, PesvRequisitosDesplazamientosService } from "../../../../../shared/services/PesvRequisitosDesplazamientos.service";
import { RequisitosDesplazamientosService } from "../../../../../shared/services/requisitosDesplazamiento.service";

@Component({
  selector: "app-requisitos-desplazamientos",
  templateUrl: "./requisitos-desplazamientos.component.html",
  styleUrls: ["./requisitos-desplazamientos.component.scss"],
})
export class RequisitosDesplazamientosComponent implements OnInit {
  // ============ TABS PRINCIPALES (formulario / listado) ============
  activeTab = 1;

  // Desplazamiento padre
  idDesplazamiento!: number;
  desplazamiento: any = null;

  get desplazamientoActivo(): boolean {
    return Number(this.desplazamiento?.estado) === 1;
  }

  requisitoForm!: FormGroup;
  guardando = false;
  modoEdicion = false;
  idRequisitoEditando: number | null = null;

  // Sugerencias de requisitos (el campo permite escribir uno nuevo)
  requisitosDesplazamientos: any[] = [];

  readonly opcionesCumple: { id: number; nombre: string }[] = [
    { id: 1, nombre: "Cumple" },
    { id: 0, nombre: "No cumple" },
  ];

  // Listado
  requisitos: PesvRequisitosDesplazamientos[] = [];
  requisitosFiltrados: PesvRequisitosDesplazamientos[] = [];
  cargandoListado = false;
  filtro = "";
  filtroCumple: number | null = null;
  page = 1;
  pageSize = 10;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private location: Location,
    private requisitosService: PesvRequisitosDesplazamientosService,
    private desplazamientosService: PesvDesplazamientosService,
    private requisitosDesplazamientosService : RequisitosDesplazamientosService,
  ) {}

  ngOnInit(): void {
    this.idDesplazamiento = Number(this.route.snapshot.paramMap.get("id"));

    if (!this.idDesplazamiento) {
      Swal.fire("Error", "No se recibió un desplazamiento válido.", "error").then(() =>
        this.volver(),
      );
      return;
    }

    this.inicializarFormulario();
    this.cargarDesplazamiento();
    this.cargarRequisitos();
    this.cargarRequisitosDesplazamientos();
  }

  private inicializarFormulario(): void {
    this.requisitoForm = this.fb.group({
      requisito: [null, Validators.required],
      cumple: [1, Validators.required],
      observaciones: [""],
    });
  }

  // ============ Desplazamiento padre ============
  private cargarDesplazamiento(): void {
    this.desplazamientosService
      .getByIdRiesgo(this.idDesplazamiento)
      .pipe(catchError(() => of(null)))
      .subscribe((respuesta: any) => {
        const data = respuesta?.data ?? respuesta;
        this.desplazamiento = Array.isArray(data) ? data[0] ?? null : data ?? null;
        this.activeTab = this.desplazamientoActivo ? 1 : 2;
      });
  }

  cargarRequisitosDesplazamientos(): void {
    this.requisitosDesplazamientosService.getAll().subscribe({
      next: (data: any) => {
        this.requisitosDesplazamientos = this.normalizarLista(data);
      },
      error: () => {
        this.requisitosDesplazamientos = [];
      },
    });
  }

  private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  // ============ Helpers de presentación ============
  nombreCumple(valor?: number | null): string {
    return Number(valor) === 1 ? "Cumple" : "No cumple";
  }

  claseCumple(valor?: number | null): string {
    return Number(valor) === 1 ? "bg-success" : "bg-danger";
  }

  // ============ Listado ============
  cargarRequisitos(): void {
    this.cargandoListado = true;
    this.requisitosService.getByDesplazamiento(this.idDesplazamiento).subscribe({
      next: (data: any) => {
        this.requisitos = this.normalizarLista(data);
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.requisitos = [];
        this.requisitosFiltrados = [];
        this.cargandoListado = false;
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.toLowerCase().trim();
    this.requisitosFiltrados = this.requisitos.filter((r) => {
      const coincideTexto =
        !termino ||
        [r.requisito, r.observaciones, this.nombreCumple(r.cumple)]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(termino));

      const coincideCumple =
        this.filtroCumple === null || Number(r.cumple) === this.filtroCumple;
      return coincideTexto && coincideCumple;
    });
    this.page = 1;
  }

  // ============ Guardar / editar ============
  private aTextoONull(valor: any): string | null {
    if (valor === null || valor === undefined) return null;
    const t = String(valor).trim();
    return t === "" ? null : t;
  }

  private construirPayload(): PesvRequisitosDesplazamientosPayload {
    const raw = this.requisitoForm.getRawValue();
    const seleccion = raw.requisito;
    const opcionCatalogo = this.requisitosDesplazamientos.find(
      (opcion) =>
        String(opcion.id) === String(seleccion) ||
        String(opcion.descripcion) === String(seleccion),
    );

    return {
      desplazamiento_id: this.idDesplazamiento,
      requisito: String(opcionCatalogo?.id ?? "").trim(),
      cumple: Number(raw.cumple) === 1 ? 1 : 0,
      observaciones: this.aTextoONull(raw.observaciones),
    };
  }

  guardar(): void {
    if (!this.modoEdicion && !this.desplazamientoActivo) {
      Swal.fire("Desplazamiento inactivo", "No se pueden crear requisitos para un desplazamiento inactivo.", "warning");
      return;
    }

    if (this.requisitoForm.invalid) {
      this.requisitoForm.markAllAsTouched();
      Swal.fire("Campos obligatorios", "Completa todos los campos requeridos.", "warning");
      return;
    }

    const payload = this.construirPayload();
    if (!payload.requisito) {
      this.requisitoForm.get("requisito")?.markAsTouched();
      Swal.fire("Campo obligatorio", "Selecciona o escribe un requisito.", "warning");
      return;
    }

    this.guardando = true;

    const obs =
      this.modoEdicion && this.idRequisitoEditando
        ? this.requisitosService.update(this.idRequisitoEditando, payload)
        : this.requisitosService.create(payload);

    obs.subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire("Éxito", "Requisito guardado correctamente.", "success");
        this.cancelarEdicion();
        this.cargarRequisitos();
        this.activeTab = 2;
      },
      error: (error) => {
        this.guardando = false;
        Swal.fire(
          "Error",
          error?.error?.message || error?.error?.error || "No se pudo guardar el requisito.",
          "error",
        );
      },
    });
  }

  editar(requisito: PesvRequisitosDesplazamientos): void {
    if (Number(requisito.estado) !== 1) {
      Swal.fire("No se puede editar", "Solo se pueden editar requisitos activos.", "warning");
      return;
    }

    if (!requisito.id) return;

    this.requisitosService.getById(requisito.id).subscribe({
      next: (respuesta: any) => {
        const data = respuesta?.data ?? respuesta;
        const detalle = Array.isArray(data) ? data[0] : data;
        if (!detalle) {
          Swal.fire("Error", "No se encontró el requisito para editar.", "error");
          return;
        }

        this.modoEdicion = true;
        this.idRequisitoEditando = detalle.id ?? requisito.id;
        this.requisitoForm.patchValue({
          requisito: detalle.requisito,
          cumple: Number(detalle.cumple) === 1 ? 1 : 0,
          observaciones: detalle.observaciones ?? "",
        });
        this.activeTab = 1;
      },
      error: () => Swal.fire("Error", "No se pudo cargar el requisito para editar.", "error"),
    });
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idRequisitoEditando = null;
    this.requisitoForm.reset({ requisito: null, cumple: 1, observaciones: "" });
    if (!this.desplazamientoActivo) this.activeTab = 2;
  }

  // ============ Eliminar ============
  eliminar(requisito: PesvRequisitosDesplazamientos): void {
    Swal.fire({
      title: "¿Inactivar requisito?",
      text: "Esta acción inactivará el requisito.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, inactivar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !requisito.id) return;
      this.requisitosService.inactivar(requisito.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Requisito inactivado.", "success");
          if (this.idRequisitoEditando === requisito.id) {
            this.cancelarEdicion();
          }
          this.cargarRequisitos();
        },
        error: () => Swal.fire("Error", "No se pudo inactivar el requisito.", "error"),
      });
    });
  }

  activar(requisito: PesvRequisitosDesplazamientos): void {
    Swal.fire({
      title: "¿Activar requisito?",
      text: "El requisito volverá a estar disponible.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, activar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !requisito.id) return;
      this.requisitosService.activar(requisito.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Requisito activado.", "success");
          this.cargarRequisitos();
        },
        error: () => Swal.fire("Error", "No se pudo activar el requisito.", "error"),
      });
    });
  }

  // ============ Navegación ============
  volver(): void {
    this.location.back();
  }

  // ============ Exportar ============
  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }
}